/**
 * Apply GET /v1/config theme.accent_color onto the CSS token layer.
 *
 * No color values are declared here — every derived token is expressed as an
 * opacity mix of the config accent (`color-mix(... , transparent)`), with a
 * small white mix only at the far end of gradients.
 */

/** Accent knob every override below is derived from. */
const ACCENT_VAR = '--avatar-accent';

/** Opacity of the accent, as a share of `transparent`. */
function accentAlpha(percent: number): string {
  return `color-mix(in srgb, var(${ACCENT_VAR}) ${percent}%, transparent)`;
}

/** Accent lifted with a touch of white — gradient tail only. */
function accentLift(percent: number): string {
  return `color-mix(in srgb, var(${ACCENT_VAR}) ${percent}%, white)`;
}

/** Derived tokens, in the order tokens.css declares them. */
function accentOverrides(): Record<string, string> {
  return {
    // Brand ramp: one hue, progressively lifted toward the gradient tail
    '--avatar-color-brand-red': `var(${ACCENT_VAR})`,
    '--avatar-color-brand-orange': accentLift(88),
    '--avatar-color-brand-yellow': accentLift(72),
    '--avatar-color-brand-orange-deep': `var(${ACCENT_VAR})`,
    // Panel surfaces: a whisper of accent on the page (keep chips lighter)
    '--avatar-color-cream': accentLift(4),
    '--avatar-color-cream-soft': accentLift(2),
    '--avatar-alpha-brand-35': accentAlpha(35),
    '--avatar-alpha-orange-30': accentAlpha(30),
    // Suggestion chip icons — separated by opacity, not by hue
    '--avatar-icon-robot-bg': accentAlpha(10),
    '--avatar-icon-sparkles-bg': accentAlpha(14),
    '--avatar-icon-target-bg': accentAlpha(8),
    '--avatar-icon-rocket-bg': accentAlpha(12),
    '--avatar-icon-accent': `var(${ACCENT_VAR})`,
    '--avatar-icon-accent-warm': accentLift(88),
    '--avatar-focus-ring': `var(${ACCENT_VAR})`,
  };
}

function isSupportedColor(value: string): boolean {
  if (typeof CSS === 'undefined' || !CSS.supports) return false;
  return CSS.supports('color', value);
}

/**
 * Point the token layer at the config accent (e.g. `#1E5BFF`).
 * Invalid / missing values are ignored so tokens.css defaults stay.
 */
export function applyConfigAccent(
  accentColor: string | undefined,
  root: HTMLElement = document.documentElement,
): void {
  const accent = accentColor?.trim();
  if (!accent || !isSupportedColor(accent)) return;

  root.style.setProperty(ACCENT_VAR, accent);

  // Without color-mix the accent still applies; derived tints keep defaults.
  if (!isSupportedColor(accentAlpha(30))) {
    root.style.setProperty('--avatar-color-brand-red', `var(${ACCENT_VAR})`);
    root.style.setProperty('--avatar-icon-accent', `var(${ACCENT_VAR})`);
    root.style.setProperty('--avatar-icon-accent-warm', `var(${ACCENT_VAR})`);
    return;
  }

  const overrides = accentOverrides();
  for (const [token, value] of Object.entries(overrides)) {
    root.style.setProperty(token, value);
  }
}

export function clearConfigAccent(
  root: HTMLElement = document.documentElement,
): void {
  root.style.removeProperty(ACCENT_VAR);
  for (const token of Object.keys(accentOverrides())) {
    root.style.removeProperty(token);
  }
}

const FONT_VAR = '--avatar-font-family';
const SYSTEM_FALLBACK =
  "'Segoe UI', system-ui, -apple-system, BlinkMacSystemFont, sans-serif";

/**
 * Apply GET /v1/config theme.font_family (BR-05–07). `system` keeps the
 * tokens.css default (Figtree, D-04); a safelisted face goes first, with the
 * OS stack behind it in case the font file fails to load.
 */
export function applyConfigFont(
  fontFamily: string | undefined,
  root: HTMLElement = document.documentElement,
): void {
  if (!fontFamily || fontFamily === 'system') {
    root.style.removeProperty(FONT_VAR);
    return;
  }
  root.style.setProperty(FONT_VAR, `'${fontFamily}', ${SYSTEM_FALLBACK}`);
}

const baseLanguage = (value: string): string =>
  value.trim().replace(/_/g, '-').toLowerCase().split('-')[0] ?? '';

/**
 * Panel language (BR-09): host-requested (boot / SDK `locale`) if supported,
 * else the first browser/device language that is supported, else
 * config.locale_default.
 */
export function resolveConfigLocale(options: {
  requested?: string | null;
  browserLanguages?: readonly string[];
  localeDefault?: string;
  localesSupported?: string[];
}): string | null {
  const supported = new Set((options.localesSupported ?? []).map(baseLanguage));
  const candidates = [options.requested, ...(options.browserLanguages ?? [])];
  for (const candidate of candidates) {
    if (!candidate) continue;
    const base = baseLanguage(candidate);
    if (supported.has(base)) return base;
  }

  return options.localeDefault ? baseLanguage(options.localeDefault) : null;
}
